import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error("Missing reset token.");
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords don't match.");
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. Sign in with your new password.");
      setTimeout(() => navigate("/signin"), 1200);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't reset password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen grain-overlay">
      <SiteHeader />
      <main className="max-w-md mx-auto px-6 py-16">
        <p className="label-eyebrow">Reset password</p>
        <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl tracking-tighter">
          Pick a new password.
        </h1>

        {!token ? (
          <div
            data-testid="reset-missing-token"
            className="mt-8 rounded-2xl border border-[#C05A46]/40 bg-[#C05A46]/10 p-6"
          >
            <p className="font-serif-display text-2xl">This link is missing a token.</p>
            <p className="mt-2 text-sm text-[#4A4341]">
              Please request a fresh reset link.
            </p>
            <Link
              to="/forgot-password"
              data-testid="reset-request-new-link"
              className="mt-4 inline-flex items-center gap-2 text-sm text-[#C05A46] hover:underline"
            >
              Request a reset link <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : done ? (
          <div
            data-testid="reset-success"
            className="mt-8 rounded-2xl border border-[#7A8B76]/40 bg-[#7A8B76]/10 p-6"
          >
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#7A8B76] text-white">
              <CheckCircle2 className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <p className="mt-4 font-serif-display text-2xl">All set.</p>
            <p className="mt-2 text-sm text-[#4A4341]">
              Your password has been updated. Redirecting you to sign in…
            </p>
          </div>
        ) : (
          <>
            <p className="mt-3 text-sm text-[#4A4341]">
              This link expires 30 minutes after it was requested. Choose a password with at least 8 characters.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <Label htmlFor="reset-password">New password</Label>
                <Input
                  id="reset-password"
                  type="password"
                  data-testid="reset-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]"
                />
              </div>
              <div>
                <Label htmlFor="reset-confirm">Confirm password</Label>
                <Input
                  id="reset-confirm"
                  type="password"
                  data-testid="reset-confirm-input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  className="mt-1 bg-white border-black/10 focus-visible:ring-1 focus-visible:ring-[#C05A46]"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="reset-submit-btn"
                className="btn-brand w-full rounded-full py-6 text-sm font-semibold inline-flex items-center justify-center gap-2"
              >
                {submitting ? "Updating…" : "Update password"}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
